/**
 * The public-link page shell, «Carta» look (spec §4) — the product version of the lab's
 * `pubblica/Shell.tsx` with its `carta` skin only. A public page is built from these parts
 * and never styles anything itself: Corpo Titolo Sezione Testo Elenco Fatti Azioni Nota
 * Collegamento Contatti Esito.
 *
 * Carta = the email continued: paper ground, the sede name in Georgia on a #013220 band,
 * one white card, a band footer. Every token cites landings/src/pages/ConfirmSlotPage.tsx.
 * No emoji, icon medallions, gradients, spinners or `dark:` tones on the white card.
 * Static imports only: public pages are never lazy (CLAUDE.md).
 */
import type { CSSProperties, ReactNode } from 'react'

/** ConfirmSlotPage.tsx:140 — the landings build has no `font-georgia`; inline, as that page does. */
export const GEORGIA: CSSProperties = { fontFamily: "Georgia, 'Times New Roman', serif" }

/** The sede whose site the page is on (header band + footer). */
export interface Mittente {
  nome: string
  citta: string
  dominio: string
  telefono: string
  email: string
}

// SKIN.carta (lab pubblica/Shell.tsx) — every token cites landings/src/pages/ConfirmSlotPage.tsx
const SKIN = {
  pagina: 'bg-[#f5f5f0] text-gray-900', // :138
  colonna: 'max-w-lg', // :149
  carta: 'bg-white rounded-lg shadow-sm overflow-hidden', // :195
  corpo: 'p-6 sm:p-8 space-y-6', // :227 `p-8`
  titolo: 'text-[22px] leading-snug text-gray-900', // Georgia via style
  lead: 'mt-2 text-sm text-gray-500 leading-relaxed', // :231
  sezione: 'text-[15px] text-gray-900 mb-2.5', // Georgia via style
  testo: 'text-sm text-gray-500 leading-relaxed', // :162
  voce: 'text-sm text-gray-900',
  etichetta: 'text-xs text-gray-400 uppercase tracking-wider font-semibold', // :206
  valore: 'text-sm text-gray-900 font-medium text-right break-words',
  righe: 'space-y-2.5',
  primaria:
    'w-full py-3.5 bg-[#013220] text-white text-sm font-semibold rounded-lg hover:bg-[#016432] transition-colors disabled:opacity-40 disabled:cursor-not-allowed', // :266
  secondaria:
    'w-full py-3 bg-gray-100 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
  link: 'text-sm text-[#013220] font-semibold hover:underline', // :167
  nota: 'text-xs text-gray-400 text-center', // :270
  esitoTitolo: 'text-xl text-gray-900', // :161, Georgia instead of semibold sans
  esitoTesto: 'mt-2 text-sm text-gray-500 leading-relaxed', // :162
}

export const telHref = (tel: string) => `tel:${tel.replace(/\s+/g, '')}`

/** The page: ground, header band (sede name + page kind), the card, footer band. */
export function PaginaPubblica({ mittente, tipo, children }: { mittente: Mittente; tipo: string; children: ReactNode }) {
  const luogo = [mittente.nome, mittente.citta].filter(Boolean).join(' — ')
  return (
    <div className={`min-h-screen flex flex-col ${SKIN.pagina}`}>
      {/* ConfirmSlotPage.tsx:139-146 */}
      <header className="bg-[#013220] px-6 py-8 text-center">
        <h1 className="text-2xl font-light text-white tracking-[3px]" style={GEORGIA}>
          {mittente.nome}
        </h1>
        <p className="text-[11px] text-white/50 tracking-[3px] uppercase mt-2">{tipo}</p>
      </header>
      <main className="flex-1 flex items-start justify-center px-4 py-8 sm:py-10">
        <div className={`w-full ${SKIN.colonna}`}>
          <div className={SKIN.carta}>{children}</div>
        </div>
      </main>
      {/* :279-281 */}
      <footer className="bg-[#013220] px-6 py-5 text-center">
        <p className="text-xs text-white/40">
          {luogo}
          {mittente.dominio && ` · ${mittente.dominio}`}
        </p>
      </footer>
    </div>
  )
}

/** The card's body: padding, sections spaced evenly. */
export function Corpo({ children }: { children: ReactNode }) {
  return <div className={SKIN.corpo}>{children}</div>
}

/** Title block: Georgia title, lead. */
export function Titolo({ titolo, lead }: { titolo: ReactNode; lead?: ReactNode }) {
  return (
    <div>
      <h2 className={SKIN.titolo} style={GEORGIA}>
        {titolo}
      </h2>
      {lead && <p className={SKIN.lead}>{lead}</p>}
    </div>
  )
}

/** A section: Georgia heading in sentence case, then its content. */
export function Sezione({ titolo, children }: { titolo: ReactNode; children: ReactNode }) {
  return (
    <section>
      <h3 className={SKIN.sezione} style={GEORGIA}>
        {titolo}
      </h3>
      {children}
    </section>
  )
}

export function Testo({ children }: { children: ReactNode }) {
  return <p className={SKIN.testo}>{children}</p>
}

/** A plain list, body type. */
export function Elenco({ voci }: { voci: ReactNode[] }) {
  return (
    <ul className={`space-y-1 ${SKIN.voce}`}>
      {voci.map((v, i) => (
        <li key={i}>{v}</li>
      ))}
    </ul>
  )
}

/** Fact rows: label left, value right. */
export function Fatti({ righe }: { righe: { label: ReactNode; valore: ReactNode }[] }) {
  return (
    <dl className={SKIN.righe}>
      {righe.map((r, i) => (
        <div key={i} className="flex justify-between items-baseline gap-4">
          <dt className={`${SKIN.etichetta} shrink-0`}>{r.label}</dt>
          <dd className={`${SKIN.valore} min-w-0`}>{r.valore}</dd>
        </div>
      ))}
    </dl>
  )
}

interface Azione {
  label: ReactNode
  onClick: () => void
  disabled?: boolean
}

/** Primary (and optional secondary) action, stacked full width. */
export function Azioni({ primaria, secondaria }: { primaria: Azione; secondaria?: Azione }) {
  return (
    <div className="space-y-2">
      <button type="button" onClick={primaria.onClick} disabled={primaria.disabled} className={SKIN.primaria}>
        {primaria.label}
      </button>
      {secondaria && (
        <button type="button" onClick={secondaria.onClick} disabled={secondaria.disabled} className={SKIN.secondaria}>
          {secondaria.label}
        </button>
      )}
    </div>
  )
}

export function Nota({ children }: { children: ReactNode }) {
  return <p className={SKIN.nota}>{children}</p>
}

/** A link in the look's link tone; opens another site in a new tab, no referrer. */
export function Collegamento({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={SKIN.link}>
      {children}
    </a>
  )
}

/** A sede's phone + email: one centred row (`riga`) or stacked. Empty values are skipped. */
export function Contatti({ telefono, email, riga = false }: { telefono: string; email: string; riga?: boolean }) {
  const cls = `${SKIN.link} ${riga ? '' : 'block'}`
  return (
    <div className={riga ? 'flex flex-wrap items-center justify-center gap-x-6 gap-y-1' : 'space-y-2 text-center'}>
      {telefono && (
        <a href={telHref(telefono)} className={cls}>
          {telefono}
        </a>
      )}
      {email && (
        <a href={`mailto:${email}`} className={cls}>
          {email}
        </a>
      )}
    </div>
  )
}

/** An end state: Georgia title (or the sentence itself in its place), optional contacts — no icon. */
export function Esito({
  titolo,
  testo,
  contatti,
}: {
  titolo?: ReactNode
  testo: ReactNode
  contatti?: { telefono: string; email: string }
}) {
  return (
    <div className="px-6 py-10 sm:px-8 sm:py-12 text-center">
      {titolo && (
        <p className={SKIN.esitoTitolo} style={GEORGIA}>
          {titolo}
        </p>
      )}
      <p className={titolo ? SKIN.esitoTesto : SKIN.esitoTitolo} style={titolo ? undefined : GEORGIA}>
        {testo}
      </p>
      {contatti && (contatti.telefono || contatti.email) && (
        <div className="mt-6">
          <Contatti {...contatti} />
        </div>
      )}
    </div>
  )
}
